import { useEffect, useState, useMemo } from "react";
import { itx } from "../translationItx/itx";
import BaseDialog from "./BaseDialog";
import type { ImportSummary, ImportSummaryItem, ChangeDetail } from "../etsFunctions/etsReadback";
import { downloadData } from "../export";
import { isUiModeDeveloper } from "../uiMode";

// ─── Shared style tokens ──────────────────────────────────────────────────────

const S = {
  green: "var(--knxColorGreen, #2e9e5b)",
  orange: "var(--primary-color, #e07b00)",
  oldVal: "var(--knxColorOrange, #c97a00)",
  border: "var(--border-color, rgba(0,0,0,0.10))",
  bg: "var(--bg-secondary, rgba(0,0,0,0.035))",
  radius: "6px",
} as const;

// ─── Single change row ────────────────────────────────────────────────────────

function getChangeLabel(c: ChangeDetail): string {
  const name = c.fieldName || c.field;
  if (!isUiModeDeveloper()) return name;

  const ids = [c.fieldId && c.fieldId !== name ? c.fieldId : undefined, c.moduleId && c.moduleId !== c.moduleName ? c.moduleId : undefined].filter(Boolean);
  if (ids.length === 0) return name;

  const modulePart = c.moduleName ? `${c.moduleName}: ` : "";
  return `${modulePart}${name} (${ids.join(" / ")})`;
}

// ─── Change detail row ────────────────────────────────────────────────────────

function ChangeDetailRow({ c }: { c: ChangeDetail }) {
  const label = getChangeLabel(c);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(80px,auto) 1fr auto 1fr",
        gap: "0 8px",
        alignItems: "baseline",
        padding: "2px 0",
        fontSize: "0.82em",
        lineHeight: 1.4,
      }}
    >
      <span style={{ opacity: 0.6, whiteSpace: "nowrap", fontStyle: "italic" }}>{label}</span>
      <span
        style={{
          color: S.oldVal,
          textDecoration: "line-through",
          fontFamily: "monospace",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={c.before}
      >
        {c.before}
      </span>
      <span style={{ opacity: 0.4, fontSize: "0.9em", flexShrink: 0 }}>→</span>
      <span
        style={{
          color: S.green,
          fontFamily: "monospace",
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={c.after}
      >
        {c.after}
      </span>
    </div>
  );
}

// ─── Item detail list (paginated) ─────────────────────────────────────────────

const BATCH_SIZE = 15;
const MAX_CHANGES_PER_ITEM = 60;
const downloadedSummaries = new WeakSet<ImportSummary>();

function downloadItem(item: ImportSummaryItem): ImportSummaryItem {
  const { allChanges, changes, ...rest } = item;
  return {
    ...rest,
    changes: changes?.map(downloadChange),
  };
}

function downloadChange(change: ChangeDetail): ChangeDetail {
  const { isParameter, isDefaultBackfill, ...rest } = change;
  return rest;
}

function safeDownloadName(name: string | undefined, fallback: string): string {
  const value = String(name || fallback)
    .replace(/[<>:"/\\|?*]+/g, "-")
    .trim();
  return value || fallback;
}

function readbackDownloadName(readbackData: any): string {
  return safeDownloadName(
    readbackData?.projectName || readbackData?.etsProjectName || readbackData?.name,
    "ets-readback",
  );
}

function buildReadbackChangelog(summary: ImportSummary) {
  const sections = [
    {
      category: "Group Addresses",
      createdCount: summary.groupAddressesCreated,
      modifiedCount: summary.groupAddressesModified,
      created: (summary.details?.groupAddressesCreated || []).map(downloadItem),
      modified: (summary.details?.groupAddressesModified || []).map(downloadItem),
    },
    {
      category: "Actors",
      createdCount: summary.actorsCreated,
      modifiedCount: summary.actorsModified,
      created: (summary.details?.actorsCreated || []).map(downloadItem),
      modified: (summary.details?.actorsModified || []).map(downloadItem),
    },
    {
      category: "Buttons / Sensors",
      createdCount: summary.buttonsCreated,
      modifiedCount: summary.buttonsModified,
      created: (summary.details?.buttonsCreated || []).map(downloadItem),
      modified: (summary.details?.buttonsModified || []).map(downloadItem),
    },
    {
      category: "Floors",
      createdCount: summary.floorsCreated,
      modifiedCount: 0,
      created: (summary.details?.floorsCreated || []).map(downloadItem),
      modified: [],
    },
    {
      category: "Rooms",
      createdCount: summary.roomsCreated,
      modifiedCount: 0,
      created: (summary.details?.roomsCreated || []).map(downloadItem),
      modified: [],
    },
    {
      category: "Cabinets",
      createdCount: summary.cabinetsCreated,
      modifiedCount: 0,
      created: (summary.details?.cabinetsCreated || []).map(downloadItem),
      modified: [],
    },
    {
      category: "Functions / Consumers",
      createdCount: summary.consumersCreated,
      modifiedCount: 0,
      created: (summary.details?.consumersCreated || []).map(downloadItem),
      modified: [],
    },
    {
      category: "Channel -> Consumer Connections",
      createdCount: summary.channelConnectionsAdded,
      modifiedCount: 0,
      created: (summary.details?.channelConnectionsAdded || []).map(downloadItem),
      modified: [],
    },
    {
      category: "Group Address Connections",
      createdCount: summary.gaConnectionsAdded,
      modifiedCount: 0,
      created: (summary.details?.gaConnectionsAdded || []).map(downloadItem),
      modified: [],
    },
    {
      category: "Device Structure Assignments",
      createdCount: 0,
      modifiedCount: summary.devicesMovedInStructure ?? 0,
      created: [],
      modified: (summary.details?.devicesMovedInStructure || []).map(downloadItem),
    },
  ];

  const totalCreated = sections.reduce((sum, section) => sum + section.createdCount, 0);
  const totalModified = sections.reduce((sum, section) => sum + section.modifiedCount, 0);

  return {
    type: "ets-readback-changelog",
    generatedAt: new Date().toISOString(),
    totals: {
      created: totalCreated,
      modified: totalModified,
      changes: totalCreated + totalModified,
    },
    sections,
  };
}

function getItemChanges(item: ImportSummaryItem, showAllParameterDiffs: boolean): ChangeDetail[] {
  return showAllParameterDiffs && item.allChanges ? item.allChanges : item.changes || [];
}

function changeMatchesSearch(change: ChangeDetail, search: string): boolean {
  if (!search) return true;
  const text = [getChangeLabel(change), change.fieldId, change.moduleId, change.before, change.after]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return text.includes(search);
}

function getVisibleItemChanges(
  item: ImportSummaryItem,
  showAllParameterDiffs: boolean,
  search: string,
): ChangeDetail[] {
  const changes = getItemChanges(item, showAllParameterDiffs);
  if (!search) return changes;
  return changes.filter((change) => changeMatchesSearch(change, search));
}

function itemMatchesSearch(item: ImportSummaryItem, search: string, showAllParameterDiffs: boolean): boolean {
  if (!search) return true;
  if (getVisibleItemChanges(item, showAllParameterDiffs, search).length > 0) return true;
  const text = [item.name, item.tag, item.address].filter(Boolean).join(" ").toLowerCase();
  return text.includes(search);
}

function ItemList({
  items,
  search,
  showAllParameterDiffs,
}: {
  items: ImportSummaryItem[];
  search: string;
  showAllParameterDiffs: boolean;
}) {
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  // Track which items have their render-cap overridden (show all changes beyond MAX_CHANGES_PER_ITEM)
  const [renderCapExpanded, setRenderCapExpanded] = useState<Set<number>>(() => new Set());
  // Track which items have their hidden parameter diffs expanded inline
  const [paramDiffsExpanded, setParamDiffsExpanded] = useState<Set<number>>(() => new Set());

  const normalizedSearch = search.trim().toLowerCase();
  const filteredItems = useMemo(
    () => items.filter((item) => itemMatchesSearch(item, normalizedSearch, showAllParameterDiffs)),
    [items, normalizedSearch, showAllParameterDiffs],
  );

  useEffect(() => {
    setVisibleCount(BATCH_SIZE);
    setRenderCapExpanded(new Set());
    setParamDiffsExpanded(new Set());
  }, [filteredItems]);

  useEffect(() => {
    if (visibleCount >= filteredItems.length) return;
    const id = requestAnimationFrame(() => {
      setVisibleCount((n) => Math.min(n + BATCH_SIZE, filteredItems.length));
    });
    return () => cancelAnimationFrame(id);
  }, [visibleCount, filteredItems.length]);

  const visible = filteredItems.slice(0, visibleCount);
  const remaining = filteredItems.length - visibleCount;

  if (filteredItems.length === 0) {
    return (
      <div style={{ padding: "8px 12px", opacity: 0.5, fontStyle: "italic", fontSize: "0.82em" }}>
        {itx("etsReadback.noResults", "No matching items")}
      </div>
    );
  }

  return (
    <div style={{ maxHeight: "300px", overflowY: "auto", padding: "4px 0" }}>
      {visible.map((item, i) => {
        const itemChanges = getVisibleItemChanges(item, showAllParameterDiffs, normalizedSearch);
        const isRenderCapExpanded = renderCapExpanded.has(i);
        const cappedChanges = isRenderCapExpanded ? itemChanges : itemChanges.slice(0, MAX_CHANGES_PER_ITEM);
        const hiddenByRenderCap = Math.max(0, itemChanges.length - MAX_CHANGES_PER_ITEM);

        // Parameter diffs: available when showAllParameterDiffs is OFF and item.allChanges exists
        const isParamExpanded = paramDiffsExpanded.has(i);
        const hiddenParamChanges =
          !showAllParameterDiffs && isParamExpanded && item.allChanges
            ? item.allChanges.filter((c) => !item.changes?.includes(c))
            : [];
        const hiddenParamCount =
          !showAllParameterDiffs && !isParamExpanded && item.hiddenChangeCount ? item.hiddenChangeCount : 0;

        const isLast = i === visible.length - 1 && remaining === 0;

        return (
          <div key={i} style={{ padding: "6px 12px", borderBottom: isLast ? "none" : `1px solid ${S.border}` }}>
            {/* Device / GA header row */}
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
              {item.tag && (
                <code
                  style={{
                    background: "rgba(var(--primary-color-rgb, 224,123,0), 0.12)",
                    color: S.orange,
                    borderRadius: "4px",
                    padding: "0 5px",
                    fontSize: "0.8em",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {item.tag}
                </code>
              )}
              <span style={{ flexGrow: 1, fontWeight: 500, fontSize: "0.85em" }}>{item.name}</span>
              {item.address && (
                <span
                  style={{
                    opacity: 0.5,
                    flexShrink: 0,
                    fontFamily: "monospace",
                    fontSize: "0.8em",
                    background: S.bg,
                    borderRadius: "4px",
                    padding: "1px 6px",
                  }}
                >
                  {item.address}
                </span>
              )}
            </div>

            {/* Per-field change rows */}
            {cappedChanges.length > 0 && (
              <div style={{ marginTop: "4px", paddingLeft: "8px", borderLeft: `2px solid ${S.border}` }}>
                {cappedChanges.map((c, j) => (
                  <ChangeDetailRow key={j} c={c} />
                ))}
              </div>
            )}

            {/* "N more changes" expand button (render cap) */}
            {!isRenderCapExpanded && hiddenByRenderCap > 0 && (
              <button
                onClick={() => setRenderCapExpanded((prev) => new Set([...prev, i]))}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "4px",
                  marginLeft: "8px",
                  background: "none",
                  border: `1px solid ${S.border}`,
                  borderRadius: "10px",
                  padding: "1px 8px",
                  fontSize: "0.78em",
                  cursor: "pointer",
                  color: S.orange,
                  opacity: 0.85,
                }}
              >
                +{hiddenByRenderCap} {itx("etsReadback.moreChanges", "more changes")} —{" "}
                {itx("etsReadback.showAll", "Show all")}
              </button>
            )}

            {/* Expanded parameter diffs inline */}
            {isParamExpanded && hiddenParamChanges.length > 0 && (
              <div style={{ marginTop: "4px", paddingLeft: "8px", borderLeft: `2px dashed ${S.border}` }}>
                {hiddenParamChanges.map((c, j) => (
                  <ChangeDetailRow key={j} c={c} />
                ))}
              </div>
            )}

            {/* "+N parameter diffs" expand button */}
            {hiddenParamCount > 0 && !isParamExpanded && (
              <button
                onClick={() => setParamDiffsExpanded((prev) => new Set([...prev, i]))}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "4px",
                  marginLeft: "8px",
                  background: "none",
                  border: `1px solid ${S.border}`,
                  borderRadius: "10px",
                  padding: "1px 8px",
                  fontSize: "0.78em",
                  cursor: "pointer",
                  color: S.orange,
                  opacity: 0.85,
                }}
              >
                +{hiddenParamCount} {itx("etsReadback.paramDiffs", "parameter differences")} —{" "}
                {itx("etsReadback.showAll", "Show all")}
              </button>
            )}
            {isParamExpanded && (
              <button
                onClick={() =>
                  setParamDiffsExpanded((prev) => {
                    const s = new Set(prev);
                    s.delete(i);
                    return s;
                  })
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "4px",
                  marginLeft: "8px",
                  background: "none",
                  border: `1px solid ${S.border}`,
                  borderRadius: "10px",
                  padding: "1px 8px",
                  fontSize: "0.78em",
                  cursor: "pointer",
                  opacity: 0.6,
                }}
              >
                {itx("etsReadback.hideParamDiffs", "Hide parameter differences")}
              </button>
            )}
          </div>
        );
      })}
      {remaining > 0 && (
        <div style={{ padding: "6px 12px", opacity: 0.45, fontStyle: "italic", fontSize: "0.8em" }}>
          {itx("etsReadback.loadingMore", "Loading")} {remaining} {itx("etsReadback.loadingMoreSuffix", "more…")}
        </div>
      )}
    </div>
  );
}

// ─── Count badge ──────────────────────────────────────────────────────────────

function CountBadge({
  count,
  prefix,
  color,
  expanded,
  onClick,
}: {
  count: number;
  prefix: string;
  color: string;
  expanded: boolean;
  onClick: () => void;
}) {
  if (count === 0) {
    return <span style={{ opacity: 0.3, fontSize: "0.85em" }}>–</span>;
  }
  return (
    <button
      onClick={onClick}
      title={itx("etsReadback.clickToExpand", "Click to show details")}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        background: expanded ? color : "transparent",
        color: expanded ? "#fff" : color,
        border: `1.5px solid ${color}`,
        borderRadius: "12px",
        padding: "1px 9px",
        fontSize: "0.8em",
        fontWeight: 700,
        cursor: "pointer",
        transition: "background 0.15s, color 0.15s",
        lineHeight: 1.6,
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
    >
      {prefix}
      {count}
    </button>
  );
}

// ─── Expandable category row ──────────────────────────────────────────────────

type CategoryRowProps = {
  label: string;
  created: number;
  modified: number;
  detailsCreated?: ImportSummaryItem[];
  detailsModified?: ImportSummaryItem[];
  showModifiedCol: boolean;
  search: string;
  showAllParameterDiffs: boolean;
};

function CategoryRow({
  label,
  created,
  modified,
  detailsCreated,
  detailsModified,
  showModifiedCol,
  search,
  showAllParameterDiffs,
}: CategoryRowProps) {
  const [expanded, setExpanded] = useState<"created" | "modified" | null>(null); // no itx

  const hasData = created > 0 || modified > 0;
  if (!hasData) return null;

  const toggle = (
    which: "created" | "modified", // no itx
  ) => setExpanded((prev) => (prev === which ? null : which));

  const shownItems =
    expanded === "created"
      ? detailsCreated // no itx
      : expanded === "modified"
        ? detailsModified // no itx
        : null;

  const rowBg = expanded ? S.bg : "transparent";

  return (
    <div style={{ borderBottom: `1px solid ${S.border}` }}>
      {/* Summary row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "7px 12px",
          background: rowBg,
          transition: "background 0.12s",
          gap: "10px",
        }}
      >
        <span style={{ flex: 1, fontWeight: 500, fontSize: "0.9em" }}>{label}</span>
        <CountBadge
          count={created}
          prefix="+"
          color={S.green}
          expanded={expanded === "created"} // no itx
          onClick={() => toggle("created")} // no itx
        />
        {showModifiedCol && (
          <CountBadge
            count={modified}
            prefix="~"
            color={S.orange}
            expanded={expanded === "modified"} // no itx
            onClick={() => toggle("modified")} // no itx
          />
        )}
      </div>
      {/* Detail panel */}
      {shownItems && shownItems.length > 0 && (
        <div
          style={{
            borderTop: `1px solid ${S.border}`,
            background: S.bg,
          }}
        >
          <ItemList items={shownItems} search={search} showAllParameterDiffs={showAllParameterDiffs} />
        </div>
      )}
    </div>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

export default function EtsReadbackResultDialog(props: {
  summary: ImportSummary;
  preview?: boolean;
  readbackData?: any;
}) {
  const { summary, preview, readbackData } = props;
  const [search, setSearch] = useState("");
  const [showAllParameterDiffs, setShowAllParameterDiffs] = useState(false);

  useEffect(() => {
    if (preview) return;
    if (!isUiModeDeveloper()) return;
    if (!summary || downloadedSummaries.has(summary)) return;
    downloadedSummaries.add(summary);
    downloadData(
      JSON.stringify(buildReadbackChangelog(summary), null, 2),
      ".ets-readback-summary.json",
      readbackData ? readbackDownloadName(readbackData) : "ets-readback-summary",
    );
  }, [summary, preview, readbackData]);

  function downloadReadbackJson() {
    if (!readbackData) return;
    downloadData(JSON.stringify(readbackData), ".ets-readback.json", readbackDownloadName(readbackData));
  }

  function downloadSummaryJson() {
    downloadData(
      JSON.stringify(buildReadbackChangelog(summary), null, 2),
      ".ets-readback-summary.json",
      readbackData ? readbackDownloadName(readbackData) : "ets-readback-summary",
    );
  }

  const totalCreated =
    summary.groupAddressesCreated +
    summary.actorsCreated +
    summary.buttonsCreated +
    summary.floorsCreated +
    summary.roomsCreated +
    summary.cabinetsCreated +
    summary.consumersCreated +
    summary.channelConnectionsAdded +
    summary.gaConnectionsAdded;

  const totalModified =
    summary.groupAddressesModified +
    summary.actorsModified +
    summary.buttonsModified +
    (summary.devicesMovedInStructure ?? 0);

  const noChanges = totalCreated === 0 && totalModified === 0;

  const showModifiedCol =
    summary.groupAddressesModified > 0 ||
    summary.actorsModified > 0 ||
    summary.buttonsModified > 0 ||
    (summary.devicesMovedInStructure ?? 0) > 0;

  return (
    <BaseDialog
      hideXButton
      title={
        preview
          ? itx("etsReadback.previewDialogTitle", "ETS Readback - Changes to Apply")
          : itx("etsReadback.resultDialogTitle", "ETS Import – Result")
      }
      okCaption={preview ? itx("etsReadback.applyReadback", "Apply Readback") : undefined}
      abortCaption={preview ? itx("cancel", "Cancel") : itx("close", "Schließen")}
      noOkButton={!preview}
      onOk={() => true}
    >
      <div style={{ width: "680px", maxWidth: "90vw", display: "flex", flexDirection: "column", gap: "12px" }}>
        {/* ── Top bar: message (preview) or summary download (result) ── */}
        {preview ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              padding: "10px 14px",
              background: S.bg,
              borderRadius: S.radius,
              border: `1px solid ${S.border}`,
            }}
          >
            <span style={{ fontSize: "0.88em", opacity: 0.75, flex: 1, minWidth: "160px" }}>
              {itx(
                "etsReadback.previewDialogMessage",
                "The following changes will be applied to the current Estada project.",
              )}
            </span>
            {readbackData && (
              <button
                className="button"
                onClick={downloadReadbackJson}
                style={{ fontSize: "0.8em", padding: "3px 10px", flexShrink: 0 }}
              >
                {itx("etsReadback.downloadReadbackJson", "Download readback JSON")}
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="button" onClick={downloadSummaryJson} style={{ fontSize: "0.8em", padding: "3px 10px" }}>
              {itx("etsReadback.downloadSummaryJson", "Download summary JSON")}
            </button>
          </div>
        )}

        {noChanges ? (
          <div
            style={{
              padding: "20px 16px",
              textAlign: "center",
              opacity: 0.6,
              border: `1px solid ${S.border}`,
              borderRadius: S.radius,
            }}
          >
            {itx("etsReadback.noChanges", "No changes were made to the project.")}
          </div>
        ) : (
          <>
            {/* ── Filter bar ── */}
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  position: "relative",
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: "9px",
                    opacity: 0.4,
                    pointerEvents: "none",
                    fontSize: "0.9em",
                  }}
                >
                  🔍
                </span>
                <input
                  value={search}
                  onChange={(ev) => setSearch(ev.target.value)}
                  placeholder={itx("etsReadback.filter", "Filter")}
                  style={{
                    flex: 1,
                    paddingLeft: "28px",
                    paddingRight: search ? "28px" : undefined,
                    minWidth: "0",
                  }}
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    title={itx("etsReadback.clearFilter", "Clear filter")}
                    style={{
                      position: "absolute",
                      right: "6px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      opacity: 0.45,
                      padding: "0 3px",
                      lineHeight: 1,
                      fontSize: "1em",
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              <label
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  fontSize: "0.85em",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={showAllParameterDiffs}
                  onChange={(ev) => setShowAllParameterDiffs(ev.target.checked)}
                />
                {itx("etsReadback.showAllParamDiffs", "Show all parameter diffs")}
              </label>
            </div>

            {/* ── Category list ── */}
            <div
              style={{
                border: `1px solid ${S.border}`,
                borderRadius: S.radius,
                overflow: "hidden",
              }}
            >
              {/* Column header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "6px 12px",
                  borderBottom: `1px solid ${S.border}`,
                  background: S.bg,
                  gap: "10px",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    fontSize: "0.75em",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    opacity: 0.55,
                  }}
                >
                  {itx("etsReadback.resultColCategory", "Category")}
                </span>
                <span
                  style={{
                    fontSize: "0.75em",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: S.green,
                    minWidth: "52px",
                    textAlign: "center",
                  }}
                >
                  {itx("etsReadback.resultColCreated", "Created")}
                </span>
                {showModifiedCol && (
                  <span
                    style={{
                      fontSize: "0.75em",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: S.orange,
                      minWidth: "60px",
                      textAlign: "center",
                    }}
                  >
                    {itx("etsReadback.resultColModified", "Modified")}
                  </span>
                )}
              </div>

              {/* Rows */}
              <CategoryRow
                label={itx("etsReadback.resultGA", "Group Addresses")}
                created={summary.groupAddressesCreated}
                modified={summary.groupAddressesModified}
                detailsCreated={summary.details?.groupAddressesCreated}
                detailsModified={summary.details?.groupAddressesModified}
                showModifiedCol={showModifiedCol}
                search={search}
                showAllParameterDiffs={showAllParameterDiffs}
              />
              <CategoryRow
                label={itx("etsReadback.resultActors", "Actors")}
                created={summary.actorsCreated}
                modified={summary.actorsModified}
                detailsCreated={summary.details?.actorsCreated}
                detailsModified={summary.details?.actorsModified}
                showModifiedCol={showModifiedCol}
                search={search}
                showAllParameterDiffs={showAllParameterDiffs}
              />
              <CategoryRow
                label={itx("etsReadback.resultButtons", "Buttons / Sensors")}
                created={summary.buttonsCreated}
                modified={summary.buttonsModified}
                detailsCreated={summary.details?.buttonsCreated}
                detailsModified={summary.details?.buttonsModified}
                showModifiedCol={showModifiedCol}
                search={search}
                showAllParameterDiffs={showAllParameterDiffs}
              />
              <CategoryRow
                label={itx("etsReadback.resultFloors", "Floors")}
                created={summary.floorsCreated}
                modified={0}
                detailsCreated={summary.details?.floorsCreated}
                showModifiedCol={showModifiedCol}
                search={search}
                showAllParameterDiffs={showAllParameterDiffs}
              />
              <CategoryRow
                label={itx("etsReadback.resultRooms", "Rooms")}
                created={summary.roomsCreated}
                modified={0}
                detailsCreated={summary.details?.roomsCreated}
                showModifiedCol={showModifiedCol}
                search={search}
                showAllParameterDiffs={showAllParameterDiffs}
              />
              <CategoryRow
                label={itx("etsReadback.resultCabinets", "Cabinets")}
                created={summary.cabinetsCreated}
                modified={0}
                detailsCreated={summary.details?.cabinetsCreated}
                showModifiedCol={showModifiedCol}
                search={search}
                showAllParameterDiffs={showAllParameterDiffs}
              />
              <CategoryRow
                label={itx("etsReadback.resultConsumers", "Functions / Consumers")}
                created={summary.consumersCreated}
                modified={0}
                detailsCreated={summary.details?.consumersCreated}
                showModifiedCol={showModifiedCol}
                search={search}
                showAllParameterDiffs={showAllParameterDiffs}
              />
              <CategoryRow
                label={itx("etsReadback.resultChannelConnections", "Channel → Consumer Connections")}
                created={summary.channelConnectionsAdded}
                modified={0}
                detailsCreated={summary.details?.channelConnectionsAdded}
                showModifiedCol={showModifiedCol}
                search={search}
                showAllParameterDiffs={showAllParameterDiffs}
              />
              <CategoryRow
                label={itx("etsReadback.resultGaConnections", "Group Address Connections")}
                created={summary.gaConnectionsAdded}
                modified={0}
                detailsCreated={summary.details?.gaConnectionsAdded}
                showModifiedCol={showModifiedCol}
                search={search}
                showAllParameterDiffs={showAllParameterDiffs}
              />
              <CategoryRow
                label={itx("etsReadback.resultMovedDevices", "Device Structure Assignments")}
                created={0}
                modified={summary.devicesMovedInStructure ?? 0}
                detailsModified={summary.details?.devicesMovedInStructure}
                showModifiedCol={showModifiedCol}
                search={search}
                showAllParameterDiffs={showAllParameterDiffs}
              />

              {/* Total footer */}
              {(totalCreated > 0 || totalModified > 0) && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "7px 12px",
                    borderTop: `2px solid ${S.border}`,
                    background: S.bg,
                  }}
                >
                  <span style={{ flex: 1, fontWeight: 700, fontSize: "0.9em" }}>
                    {itx("etsReadback.resultTotal", "Total")}
                  </span>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "0.88em",
                      color: S.green,
                      minWidth: "52px",
                      textAlign: "center",
                    }}
                  >
                    {totalCreated > 0 ? `+${totalCreated}` : "–"}
                  </span>
                  {showModifiedCol && (
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: "0.88em",
                        color: S.orange,
                        minWidth: "60px",
                        textAlign: "center",
                      }}
                    >
                      {totalModified > 0 ? `~${totalModified}` : "–"}
                    </span>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </BaseDialog>
  );
}
